import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Code, Palette, Smartphone, Globe, Brain, Zap, Cloud } from 'lucide-react';
import Loader from './Loader';
import Nav from './Nav';
import Hero from './Hero';
import About from './About';
import Projects from './Projects';
import Contact from './Contact';
import Footer from './Footer';
import '../index.css';
import Project1 from '../Images/Project1.png'
import Project2 from '../Images/Project2.png'
import Project3 from '../Images/Project3.png'
import Project4 from '../Images/Project4.png'
import Project5 from '../Images/Project5.png'
import Project6 from '../Images/Project8.png'

const Home = ({ isDarkMode, setIsDarkMode }) => {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [hoverTarget, setHoverTarget] = useState(null);
  const [navScrolled, setNavScrolled] = useState(false);

  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);

  const projects = [
    {
      id: 1,
      title: "Tourism Website",
      description: "A complete tourism portal featuring safari tours, activities booking, and contact options. The website provides visitors with seamless booking for adventures, dune bashing, camel rides, and cultural experiences with real time availability.",
      tech: ["React", "Express", "Node.js", "MongoDB"],
      image: Project1,
      live: "https://safariadventuredubai.com",
      featured: true
    },
    {
      id: 2,
      title: "Music Streaming Platform",
      description: "A full featured music streaming application with audio player functionality, playlists, search, shuffle and repeat functionalities for a smooth and enjoyable user experience.",
      tech: ["React", "Redux", "Web Audio API", "Figma", "MongoDB"],
      image: Project2,
      github: "https://github.com/WaleedAbdullah2215/MusicPlayer_MUSUNG",
      live: "#",
      featured: true
    },
    {
      id: 3,
      title: "Sketch2Life, AI 3D Model Generator",
      description: "Transform 2D sketches and images into high quality 3D models using TripoSR AI. Features automatic background removal, multiple resolution options, and instant 3D preview with export capabilities.",
      tech: ["Python", "TripoSR", "PyTorch", "PyMCubes", "Trimesh", "PIL"],
      image: Project3,
      github: "https://github.com/WaleedAbdullah2215/GenAi_2dto3d",
      live: "#",
      featured: true
    },
    {
      id: 4,
      title: "Campus Navigation Guide",
      description: "An augmented reality campus navigation system that evolved from a basic prototype to a comprehensive wayfinding solution. Combines indoor and outdoor mapping with AR overlays to provide intuitive navigation for students, faculty, and visitors across university facilities.",
      tech: ["React", "ARCore", "Firebase", "FastAPI", "TensorFlow"],
      image: Project4,
      github: "#",
      live: "#",
      featured: false
    },
    {
      id: 5,
      title: "AI Powered Driving Assistance System",
      description: "An intelligent real time driving assistant using computer vision and deep learning. Detects lanes, obstacles, traffic signs, and driver drowsiness, providing live audio visual alerts to enhance road safety.",
      tech: ["Python", "OpenCV", "YOLO", "ByteTrack", "MiDaS", "Llama", "React", "Node.js"],
      image: Project5,
      github: "https://github.com/Imaduddin-Ansari/NeuroDrive/tree/Waleed_Abdullah",
      live: "#",
      featured: true
    },
    {
      id: 6,
      title: "Medication Reminder Bot",
      description: "An n8n powered Telegram bot that helps patients stay on top of their medication schedule. Scheduled reminders go out through Telegram, patients confirm each dose with a single tap, and the workflow logs adherence automatically. If a dose is missed, the bot escalates by notifying a designated caregiver, helping reduce missed doses for people managing chronic conditions or elderly care.",
      tech: ["n8n", "Telegram Bot API", "Webhooks", "Google Sheets API", "Cron Scheduling", "Twilio"],
      image: Project6,
      github: "#",
      live: "#",
      featured: true
    }
  ];

  const skills = [
    { name: "Frontend Mastery", icon: <Code />, level: 98, color: "#667eea" },
    { name: "AI/ML Integration", icon: <Brain />, level: 87, color: "#8b5cf6" },
    { name: "Mobile Development", icon: <Smartphone />, level: 85, color: "#4ade80" },
    { name: "Full Stack Architecture", icon: <Globe />, level: 96, color: "#fb923c" },
    { name: "UI/UX Innovation", icon: <Palette />, level: 95, color: "#f093fb" },
    { name: "Cloud Management", icon: <Cloud />, level: 92, color: "#06b6d4" }
  ];

  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        driftY: -100 - Math.random() * 200,
        driftX: (Math.random() - 0.5) * 100,
        duration: 20 + Math.random() * 20,
        delay: Math.random() * 10,
        hue: Math.random() * 60 + 200,
      })),
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2500);

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleScroll = () => {
      setNavScrolled(window.scrollY > 50);

      const sections = [
        { id: 'home', ref: homeRef },
        { id: 'about', ref: aboutRef },
        { id: 'projects', ref: projectsRef },
        { id: 'contact', ref: contactRef }
      ];

      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref.current && section.ref.current.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleHover = (target) => {
    setIsHovering(true);
    setHoverTarget(target);
  };

  const handleHoverEnd = () => {
    setIsHovering(false);
    setHoverTarget(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="container">
      <AnimatePresence>
        {isHovering && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            className="cursor-trail"
            style={{
              left: mousePosition.x - 25,
              top: mousePosition.y - 25,
              background: hoverTarget === 'primary'
                ? 'radial-gradient(circle, rgba(102,126,234,0.8) 0%, transparent 70%)'
                : hoverTarget === 'secondary'
                  ? 'radial-gradient(circle, rgba(240,147,251,0.8) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
            }}
          />
        )}
      </AnimatePresence>

      <div className="particles-container">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            style={{
              position: 'absolute',
              left: `${p.left}%`,
              top: `${p.top}%`,
              background: `hsla(${p.hue}, 80%, 60%, 0.3)`,
            }}
            animate={{
              y: [0, p.driftY],
              x: [0, p.driftX],
              opacity: [0, 0.6, 0]
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay
            }}
            className="floating-particle"
          />
        ))}
      </div>

      <Nav
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        navScrolled={navScrolled}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
      />

      <Hero
        homeRef={homeRef}
        setActiveSection={setActiveSection}
        handleHover={handleHover}
        handleHoverEnd={handleHoverEnd}
      />

      <About aboutRef={aboutRef} skills={skills} />

      <Projects projectsRef={projectsRef} projects={projects} />

      <Contact
        contactRef={contactRef}
        handleHover={handleHover}
        handleHoverEnd={handleHoverEnd}
      />

      <Footer />
    </div>
  );
};

export default Home;